/**
 * ProjectHeader Component
 * Logo with site navigation dropdown, and editable project name
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { DropdownPortal } from '../../ui/dropdown-portal';
import { Home, ShoppingBag, Store, CreditCard, ChevronDown } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Home', path: '/', icon: Home, description: 'Landing page' },
  { label: 'Products', path: '/products', icon: ShoppingBag, description: 'Browse products' },
  { label: 'Marketplace', path: '/marketplace', icon: Store, description: 'Presets & plugins' },
  { label: 'Pricing', path: '/pricing', icon: CreditCard, description: 'Plans & pricing' },
];

export const ProjectHeader = ({
  projectName,
  setProjectName,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  
  return (
    <div className="flex items-center gap-2 md:gap-4">
      {/* Site Navigation Dropdown */}
      <div className="relative">
        <Button
          ref={triggerRef}
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="text-zinc-400 hover:text-white p-2 flex items-center gap-1"
          data-testid="nav-site-menu"
          title="Navigate to site"
        >
          <Home className="w-5 h-5" />
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
        
        <DropdownPortal
          triggerRef={triggerRef}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          width={200}
          sideOffset={6}
        >
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
            <div className="px-3 py-2 border-b border-zinc-800">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Navigate to</span>
            </div>
            {NAV_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => {
                    navigate(link.path);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-800 transition-colors text-left"
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4 text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium text-white">{link.label}</div>
                    <div className="text-[10px] text-zinc-500">{link.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </DropdownPortal>
      </div>
      
      <div className="w-px h-6 bg-zinc-800 hidden md:block" />
      <input
        type="text"
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        className="bg-transparent text-sm md:text-lg font-bold border-b border-transparent hover:border-zinc-600 focus:border-indigo-500 outline-none px-1 max-w-[120px] md:max-w-none"
        data-testid="project-name"
      />
    </div>
  );
};

export default ProjectHeader;
